<template>
	<div
		class="claim-details max-w-4xl mx-auto px-6 py-12 bg-white rounded-2xl shadow-lg"
	>
		<!-- Header -->
		<div class="flex justify-between items-center mb-10 border-b pb-6">
			<div>
				<h1 class="text-3xl font-bold text-gray-900">Claim Details</h1>
				<p class="text-gray-600 mt-2">
					Submit a new claim or manage pending ones
				</p>
			</div>
			<div class="text-right">
				<span class="text-sm font-medium text-gray-700"
					>Pending Claims</span
				>
				<p class="text-3xl font-bold text-blue-600">5</p>
			</div>
		</div>

		<!-- Form -->
		<form @submit.prevent="handleSubmit" class="space-y-8">
			<!-- Police Report Upload -->
			<div class="bg-gray-50 p-6 rounded-xl border border-gray-200">
				<h3 class="text-xl font-semibold text-gray-800 mb-4">
					Police Report
				</h3>
				<label class="block text-sm font-medium text-gray-700 mb-2">
					Upload Official Police Abstract / Report
					<span class="text-red-500">*</span>
				</label>
				<div class="flex items-center justify-center w-full">
					<label
						class="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition"
					>
						<div
							class="flex flex-col items-center justify-center pt-5 pb-6"
						>
							<svg
								class="w-10 h-10 mb-3 text-gray-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								/>
							</svg>
							<p class="mb-2 text-sm text-gray-500">
								<span class="font-semibold"
									>Click to upload</span
								>
								or drag and drop
							</p>
							<p class="text-xs text-gray-500">
								PDF, JPG, PNG (max 10MB)
							</p>
						</div>
						<input
							type="file"
							accept=".pdf,.jpg,.jpeg,.png"
							class="hidden"
							@change="handleFileUpload"
							required
						/>
					</label>
				</div>
				<p
					v-if="form.police_report"
					class="mt-2 text-sm text-green-600"
				>
					Selected: {{ form.police_report.name }}
				</p>
			</div>

			<!-- Accident Details -->
			<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
				<!-- Accident Location -->
				<div>
					<label
						for="accident_location"
						class="block text-sm font-medium text-gray-700 mb-2"
					>
						Accident Location <span class="text-red-500">*</span>
					</label>
					<input
						id="accident_location"
						v-model="form.accident_location"
						type="text"
						placeholder="e.g., Nairobi Expressway, near Exit 10"
						required
						class="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
					/>
				</div>

				<!-- Were you under the influence? -->
				<div>
					<label class="block text-sm font-medium text-gray-700 mb-2">
						Were you under the influence of alcohol/drugs?
						<span class="text-red-500">*</span>
					</label>
					<div class="flex space-x-8">
						<label class="flex items-center">
							<input
								type="radio"
								v-model="form.drunk"
								value="yes"
								name="drunk"
								required
								class="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<span class="ml-2 text-gray-700">Yes</span>
						</label>
						<label class="flex items-center">
							<input
								type="radio"
								v-model="form.drunk"
								value="no"
								name="drunk"
								class="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<span class="ml-2 text-gray-700">No</span>
						</label>
					</div>
				</div>

				<!-- Damage Location -->
				<div>
					<label
						for="damage"
						class="block text-sm font-medium text-gray-700 mb-2"
					>
						Description of Damage
						<span class="text-red-500">*</span>
					</label>
					<textarea
						id="damage"
						v-model="form.damage"
						rows="3"
						placeholder="e.g., Front bumper dented, driver-side door scratched"
						required
						class="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-y"
					></textarea>
				</div>

				<!-- Number of Fatalities -->
				<div>
					<label
						for="no_of_fatalities"
						class="block text-sm font-medium text-gray-700 mb-2"
					>
						Number of Fatalities <span class="text-red-500">*</span>
					</label>
					<input
						id="no_of_fatalities"
						v-model.number="form.no_of_fatalities"
						type="number"
						min="0"
						required
						class="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
					/>
				</div>
			</div>

			<!-- Submit Button -->
			<div class="pt-6">
				<button
					type="submit"
					class="w-full md:w-auto px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="isSubmitting"
				>
					{{ isSubmitting ? 'Submitting...' : 'Submit Claim' }}
				</button>
			</div>
		</form>
	</div>
</template>

<script>
export default {
	data() {
		return {
			form: {
				police_report: null,
				accident_location: '',
				drunk: '',
				damage: '',
				no_of_fatalities: 0,
			},
			isSubmitting: false,
		};
	},

	methods: {
		handleFileUpload(event) {
			const file = event.target.files[0];
			if (file) {
				this.form.police_report = file;
			}
		},

		async handleSubmit() {
			if (this.isSubmitting) return;
			this.isSubmitting = true;

			try {
				// Simulate API call
				console.log('Submitting claim:', this.form);
				// await this.$store.dispatch('submitClaim', this.form)

				alert('Claim submitted successfully!');
				// Reset form or redirect
				this.$router.push('/claims'); // optional
			} catch (error) {
				alert('Error submitting claim. Please try again.');
			} finally {
				this.isSubmitting = false;
			}
		},
	},
};
</script>

<style scoped>
/* Optional extra polish */
.claim-details {
	background: linear-gradient(to bottom right, #ffffff, #f8fafc);
}
</style>
